import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Container,
  Grid,
  Typography,
  FormControl,
  Box,
  CircularProgress,
  TextField,
  IconButton,
  FormLabel,
} from "@mui/material";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Iconify from "src/components/iconify";
import { createBlobInContainer } from "src/utils/azureBlob";

// ----------------------------------------------------------------------
const fields = [
  { title: "Name", name: "name" },
  { title: "Rank", name: "rank" },
  { title: "Total Poss. Pts", name: "possiblePoints" },
  { title: "Total Win. %", name: "winPercentage" },
];

const fieldStats = [
  { name: "First Round", code: "FR", possiblePoints: 0, winPercentage: 0 },
  { name: "Second Round", code: "SR", possiblePoints: 0, winPercentage: 0 },
  { name: "Sweet 16", code: "S16", possiblePoints: 0, winPercentage: 0 },
  { name: "Elite Eight", code: "EE", possiblePoints: 0, winPercentage: 0 },
  { name: "Final Four", code: "FF", possiblePoints: 0, winPercentage: 0 },
  { name: "Championship", code: "CH", possiblePoints: 0, winPercentage: 0 },
];

export default function EditTeamPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [teamId, setTeamId] = useState(false);
  const [competition, setCompetition] = useState(null);
 
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    teamLogo: null,
    name: "",
    rank: 0,
    competition: null,
    possiblePoints: 0,
    winPercentage: 0,
    teamStats: []
  });
  const [isFilled, setFilling] = useState(true);

  useEffect(() => {
    if (searchParams.has("type")) {
      const competition = searchParams.get("type");
      setCompetition(competition)
      setData((prevState) => ({ ...prevState, competition }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.has("team_id")) {
      setLoading(true);

      let team_id = searchParams.get("team_id");
      setTeamId(Number(team_id));

      const fetchDataTeam = async () => {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/teams/${team_id}`
        );
        const teamStats = data.teamStats.length ? data.teamStats : [];
        if(!teamStats.length) {
          fetchDataRounds()
        }
        setData({ ...data, teamStats });
        return data;
      };
      fetchDataTeam()
        .finally(() => setLoading(false))
    }

    const fetchDataRounds = async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/rounds?competition=${competition}`
      );
      const setFieldStats = fieldStats.map((field, index) => {
        const round = data.find((round) => round.name === field.name);

        return {
          ...field,
          roundId: round.roundId
        }
      })
      setData((prevState) => ({ ...prevState, teamStats: setFieldStats }));
      return data;
    };
  }, [searchParams, competition]);

  useEffect(() => {
    const count = Object.values(data).filter(
      (field) => !!field && field.length <= 0
    ).length;
    if (!count || data.name) setFilling(true);
    else setFilling(false);
  }, [data]);

  const handleChange = (event) => {
    setData({
      ...data,
      [event.target.name]: event.target.value,
    });
  };

  const handleChangeStats = (event, index) => {
    const new_stats = data.teamStats;
    new_stats[index][event.target.name] = event.target.value;

    if(event.target.name === 'winPercentage') {
      new_stats[index].possiblePoints = 100 - Number(new_stats[index].winPercentage);
    }

    setData({
      ...data,
      teamStats: new_stats
    });
  };

  const uploadFiles = (event, name) => {
    if (event.target.files.length > 0) {
      const files = event.target.files;

      let uploadPromises = [];
      uploadPromises.push(createBlobInContainer(files[0]));

      if (uploadPromises.length > 0) {
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);

          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          setData((prevState) => ({
            ...prevState,
            [name]: `https://${storageAccountName}.blob.core.windows.net/${containerName}/${files[0].name}`,
          }));
        };

        uploadFiles().catch(console.error);
      }
    }
  };

  const handleSubmit = () => {
    if (!!data) {
      console.log(data, data)
      setLoading(true);
      axios
        .put(
          `${process.env.REACT_APP_API_BASE_URL}/admin/teams/${teamId}`,
          {
            ...data,
            logo: data?.logo || null,
            rank: Number(data.rank) || 0,
            possiblePoints: Number(data?.possiblePoints) || 0,
            winPercentage: Number(data?.winPercentage) || 0
          }
        )
        .then(function (response) {
          navigate(`/dashboard/teams?type=${competition}`);
        });
    }
  };

  return (
    <>
      <Helmet>
        <title> Team </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Edit Team
          </Typography>
        </Stack>

        <Card style={{ padding: 20 }}>
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <FormLabel id="card-format-label">Logo</FormLabel>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="label"
                >
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(event) => uploadFiles(event, "logo")}
                  />
                  <Iconify icon={"eva:upload-outline"} sx={{ mr: 2 }} />
                </IconButton>
                {data.teamLogo ? (
                  <img
                    style={{
                      margin: "0 auto",
                      width: 100,
                      background: "grey",
                    }}
                    alt=""
                    src={data.teamLogo}
                  />
                ) : null}
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                {fields.map((field) => (
                  <FormControl
                    key={field.name}
                    sx={{ m: 1, minWidth: 620 }}
                    size="small"
                  >
                    <TextField
                      required
                      label={field.title}
                      placeholder={field.title}
                      name={field.name}
                      value={data[field.name] || ""}
                      onChange={(e) => handleChange(e)}
                      variant="outlined"
                    />
                  </FormControl>
                ))}
              </Grid>
              <Grid item xs={12} sm={12} md={12} spacing={2}>
                {fieldStats.map((field, index) => (
                  <Grid container xs={12} sm={12} md={12 } spacing={3} sx={{mb: 2}}>
                    <Grid item xs={2} alignContent="center">{field.name} :</Grid>
                    <Grid item xs={4}>
                      <TextField
                        required
                        fullWidth
                        label="Poss. Pts"
                        placeholder="Poss. Pts"
                        name={`possiblePoints`}
                        value={data?.teamStats[index]?.possiblePoints ?? 0}
                        onChange={(e) => handleChangeStats(e, index)}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        required
                        fullWidth
                        label="Win. %"
                        placeholder="Win. %"
                        name={`winPercentage`}
                        value={data?.teamStats[index]?.winPercentage ?? 0}
                        onChange={(e) => handleChangeStats(e, index)}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Button
                  variant="contained"
                  disabled={!isFilled}
                  onClick={() => handleSubmit()}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>
          )}
        </Card>
      </Container>
    </>
  );
}
